import { useQuery } from '@tanstack/react-query';

import { readWorkspaceUsage } from '@/lib/api/generated/api';
import type { WorkspaceUsageSummary } from '@/lib/api/generated/model';
import { UsageError, isForbidden } from '../errors';
import { usageKeys } from '../queryKeys';

/**
 * One workspace's usage for a period.
 *
 * Calls the generated *function* rather than the generated hook, following the
 * workspaces feature: what this adds is which status counts as success, what a failure
 * should say, and — here — that one failure is not really a failure.
 *
 * Anything but a 200 throws rather than resolving to an empty summary. The distinction
 * matters more here than almost anywhere in the product: a reader must be able to tell
 * "this workspace spent nothing" from "we could not find out what it spent", and
 * collapsing the two renders a confident, wrong zero against a spend limit.
 */
export interface UseWorkspaceUsageOptions {
  /**
   * Whether to ask at all. Defaults to true, which is the usage page: it exists
   * only for people who may read the figures, and the route gates it.
   *
   * `false` is for a surface that is drawn for everyone and only *additionally*
   * uses the figures when the reader has them — the spend-limit markers, which
   * would otherwise spend a certain 403 on every board load for every member
   * (spec 101 FR-031). Cached results stay readable; only the request stops.
   */
  enabled?: boolean;
}

export function useWorkspaceUsage(
  workspaceId: string,
  period: string | undefined,
  { enabled = true }: UseWorkspaceUsageOptions = {},
) {
  return useQuery<WorkspaceUsageSummary>({
    enabled,
    queryKey: usageKeys.workspace(workspaceId, period),
    queryFn: async ({ signal }) => {
      const response = await readWorkspaceUsage(
        workspaceId,
        period === undefined ? {} : { period },
        { signal },
      );
      if (response.status === 200) return response.data;
      if (response.status === 403) {
        throw new UsageError(
          'forbidden',
          'Only this workspace’s owner can see what it is spending.',
        );
      }
      throw new UsageError(
        'unavailable',
        'Could not read this workspace’s usage.',
      );
    },
    // No polling (FR-032). Usage changes when runs finish, not continuously, and a
    // period dashboard is not a monitor — ordinary invalidation covers it.
    refetchOnWindowFocus: false,
    // A refusal is an answer, not a transient failure. Retrying it delays the
    // explanation the reader needs and cannot change the outcome.
    retry: (failureCount, error) => !isForbidden(error) && failureCount < 2,
  });
}
